// Every string the athlete reads, in European Portuguese (BRAND.md §5 and
// §13.5). Components import from here and never write copy inline;
// scripts/no-inline-copy.test.mjs holds that line.
//
// Tu form, feminine where the language marks it (SPEC-12, question 5),
// no emoji, sentence case.

import type { AssessmentId } from "../schema/types";
import { athleteLanguagePt } from "./admin/athleteLanguage";
import { bilanPt } from "./admin/bilan";
import { cyclePt } from "./admin/cycle";
import { bilanFormPt } from "./admin/bilanForm";
import { bodyMapPt } from "./admin/bodyMap";
import { newAccountPt } from "./admin/newAccount";

/** A paragraph of a legal page, or a point the founder still has to decide. */
export type LegalBlock = { readonly text: string } | { readonly todo: string };

export interface LegalSection {
  readonly heading: string;
  readonly blocks: readonly LegalBlock[];
}

// The Termos and the Política de privacidade (SPEC-14, the founder's
// answer of 2026-10-15). Drafts for a lawyer to review before real
// athletes sign up. They describe what the app does today and nothing
// more; what it does not do yet is a visible "Por decidir", never a promise.
const legal = {
  back: "Voltar",
  draft: "Rascunho. Este texto ainda vai ser revisto por um jurista.",
  todoLabel: "Por decidir",
  version: "Versão de 23 de outubro de 2026",
  terms: {
    title: "Termos de utilização",
    sections: [
      {
        heading: "O que é a PYF",
        blocks: [
          { text: "A PYF é uma aplicação para atletas. Respondes a questionários curtos sobre o teu corpo: perdas de urina, ciclo menstrual, energia e alimentação, sono, visão, maxilar." },
          { text: "Nesta versão, a PYF guarda as tuas respostas e mostra-te o que já fizeste. Não te mostra resultados, não faz diagnósticos e não te encaminha para um profissional." },
          { text: "A equipa clínica da PYF lê os teus resultados e as tuas respostas para decidir se te propõe um acompanhamento. Quem decide é uma pessoa, nunca a aplicação." },
          { text: "A PYF não é um serviço médico e não substitui uma consulta. Se tens uma dúvida sobre a tua saúde, fala com um médico." },
        ],
      },
      {
        heading: "Quem pode usar",
        blocks: [
          { text: "A PYF é feita para raparigas e mulheres que praticam desporto." },
          { text: "Tens de ter pelo menos 14 anos." },
          { todo: "Se uma atleta com menos de 18 anos precisa da autorização dos pais, e como a aplicação a pede." },
        ],
      },
      {
        heading: "A tua conta",
        blocks: [
          { text: "Crias a conta com o teu primeiro nome, o teu email e uma palavra-passe. Usa um email que seja teu: é com ele que entras." },
          { text: "A palavra-passe é só tua. Não a partilhes com ninguém." },
        ],
      },
      {
        heading: "As tuas respostas",
        blocks: [
          { text: "As respostas são tuas. A PYF guarda-as à medida que respondes, para que possas parar e continuar mais tarde." },
          { text: "Ninguém do teu clube e nenhuma pessoa que te treine vê as tuas respostas através da PYF." },
          { text: "A equipa da PYF vê, num espaço só de leitura, os teus dados gerais, o estado das tuas avaliações, os teus resultados e as tuas respostas, uma a uma. A política de privacidade diz quem e para quê." },
          { text: "Quando refazes um questionário, a versão anterior fica guardada nos nossos servidores. Deixa de aparecer na aplicação, para ti e para qualquer outra pessoa." },
          { todo: "Como apagas as tuas respostas e a tua conta. A aplicação ainda não o permite." },
        ],
      },
      {
        heading: "Custo",
        blocks: [
          { text: "Nesta fase de testes, usar a PYF não tem custo." },
          { todo: "Preço e condições de pagamento, quando houver uma oferta." },
        ],
      },
      {
        heading: "Uma versão de teste",
        blocks: [
          { text: "Esta é uma versão de teste da PYF. Pode ter erros, mudar ou parar." },
          { todo: "O que a PYF assume se a aplicação falhar ou se dados se perderem." },
        ],
      },
      {
        heading: "Mudanças a estes termos",
        blocks: [{ todo: "Como te avisamos de uma mudança, e com quanto tempo de antecedência." }],
      },
      {
        heading: "Lei e contacto",
        blocks: [
          { text: "Estes termos seguem a lei portuguesa." },
          { text: "Para qualquer dúvida, escreve para hello@poweryourflow.co." },
          { todo: "Este endereço ainda não existe. Falta também o nome, o NIPC e a morada da entidade responsável pela PYF, e o tribunal competente." },
        ],
      },
    ] satisfies readonly LegalSection[],
  },
  privacy: {
    title: "Política de privacidade",
    sections: [
      {
        heading: "Em resumo",
        blocks: [
          { text: "As tuas respostas são tuas. Na aplicação, só tu as vês. Não as vendemos e não as partilhamos com o teu clube, com quem te treina nem com profissionais de saúde de fora da PYF." },
          { text: "A equipa da PYF vê os teus dados gerais, o estado das tuas avaliações, os teus resultados e as tuas respostas, para acompanhar a saúde das atletas. Cada acesso fica registado." },
        ],
      },
      {
        heading: "Quem é responsável pelos teus dados",
        blocks: [{ todo: "Nome, NIPC e morada da entidade responsável pelo tratamento, e o contacto do encarregado de proteção de dados, se houver." }],
      },
      {
        heading: "Que dados guardamos",
        blocks: [
          { text: "Conta: o teu email e a tua palavra-passe. A palavra-passe é guardada pela Firebase Authentication, um serviço da Google, de forma a que ninguém a consiga ler, nem a PYF." },
          { text: "Dados gerais: o teu primeiro nome, a data de nascimento, a modalidade, a posição, o nível, o clube, os treinos e as horas de treino por semana, o tipo de competição." },
          { text: "Respostas aos questionários: perdas de urina, ciclo menstrual e os seus sintomas, energia e alimentação, sono, visão, maxilar. São dados de saúde, a categoria de dados mais protegida pela lei (artigo 9.º do RGPD)." },
          { text: "Resultados: em alguns questionários (perdas de urina, sintomas do período, sono, visão, maxilar), a PYF calcula uma pontuação a partir das tuas respostas e compara-a com um limiar. A pontuação não aparece na aplicação. Só a equipa da PYF a vê." },
          { text: "No teu telemóvel: a aplicação guarda cada resposta no dispositivo antes de a enviar, para que nada se perca quando não tens rede." },
          { text: "Registo de acessos: quando alguém da equipa da PYF abre a lista das atletas ou o perfil de uma atleta, ou exporta um deles, fica registado quem o fez e quando." },
          { text: "A aplicação não usa publicidade nem ferramentas de análise de audiência." },
        ],
      },
      {
        heading: "Para que os usamos",
        blocks: [
          { text: "Para criar e manter a tua conta, guardar as tuas respostas e mostrar-te o que já fizeste." },
          { text: "Para que a equipa da PYF acompanhe a saúde das atletas e o uso da aplicação: quem tem conta, que avaliações fez e quando, os resultados e as respostas." },
          { text: "Para que a equipa clínica decida, a partir dos resultados e das respostas, se propõe um acompanhamento a uma atleta. Um resultado acima do limiar fica assinalado para a equipa; quem decide é sempre uma pessoa. Nesta versão, para mais nada." },
          { todo: "Como a equipa clínica contacta uma atleta a quem propõe um acompanhamento." },
          { todo: "Cada novo uso dos dados (resultados mostrados à atleta, encaminhamento para um profissional, assistente) tem de ser descrito aqui antes de existir." },
        ],
      },
      {
        heading: "Com que base legal",
        blocks: [
          { todo: "A base legal de cada tipo de dados. Para os dados de saúde, o RGPD pede em regra o consentimento explícito (artigo 9.º), e a aplicação ainda não tem um passo próprio para o pedir. Falta também decidir o caso das menores." },
        ],
      },
      {
        heading: "Onde ficam os dados",
        blocks: [
          { text: "Os dados gerais e as respostas ficam na Google Cloud (Firebase), na região europe-west1, na Bélgica." },
          { text: "A aplicação chega-te através da Cloudflare. Como qualquer site, recebe o endereço IP do teu dispositivo para te entregar as páginas." },
          { todo: "Onde a Firebase Authentication guarda o email e a palavra-passe, e se algum dado sai da União Europeia." },
        ],
      },
      {
        heading: "Quem vê os teus dados",
        blocks: [
          { text: "Na aplicação, só tu vês as tuas respostas. Nenhuma outra conta de atleta as consegue ler." },
          { text: "A equipa da PYF tem um espaço próprio, fora da aplicação e só de leitura. Só entram as contas que a PYF autoriza: hoje, a da fundadora e a de um membro da equipa clínica." },
          { text: "A lista desse espaço mostra, de cada atleta: o primeiro nome, o email, o clube, a modalidade, o nível, a idade, quantas avaliações fez e quantas estão a meio, a data em que criou a conta e a da última atividade. A exportação da lista junta a data de nascimento, a posição, o estado de cada avaliação e os resultados, sem as respostas." },
          { text: "O perfil de uma atleta mostra os dados gerais completos, o estado de cada avaliação, cada resultado ao lado do seu limiar e as respostas, uma a uma, da última vez que respondeu. A exportação do perfil junta essas respostas num ficheiro." },
          { text: "Cada vez que alguém da equipa abre a lista ou um perfil, ou os exporta, fica registado quem o fez e quando. Ninguém consegue ler esse registo a partir da aplicação." },
          { todo: "Um ficheiro exportado fica no computador de quem o exportou: onde a equipa o guarda e quando o apaga." },
          { text: "Não partilhamos nada com clubes, treinadores, pais, escolas nem profissionais de saúde de fora da PYF." },
          { text: "A Google guarda os dados e a Cloudflare entrega a aplicação, por conta da PYF." },
          { todo: "Que outras pessoas da equipa da PYF podem vir a entrar nesse espaço, com que função, e os acordos de tratamento de dados assinados com a Google e a Cloudflare." },
        ],
      },
      {
        heading: "Durante quanto tempo",
        blocks: [{ todo: "Durante quanto tempo guardamos a conta, as respostas, as versões anteriores dos questionários e o registo de acessos." }],
      },
      {
        heading: "Os teus direitos",
        blocks: [
          { text: "Tens direito a aceder aos teus dados, a corrigi-los, a pedir que sejam apagados, a recebê-los num formato que possas levar contigo, a opor-te ao seu tratamento e a retirar o teu consentimento." },
          { text: "Para isso, escreve para hello@poweryourflow.co." },
          { todo: "Este endereço ainda não existe. A aplicação ainda não permite apagar as respostas nem a conta: falta decidir como e em que prazo a PYF responde a cada pedido." },
          { text: "Podes também apresentar queixa à Comissão Nacional de Proteção de Dados (CNPD), em www.cnpd.pt." },
        ],
      },
      {
        heading: "Mudanças a esta política",
        blocks: [{ todo: "Como te avisamos de uma mudança a esta política." }],
      },
    ] satisfies readonly LegalSection[],
  },
} as const;

/** The words of one question of an assessment; ids are the lib's. */
export interface QuestionText {
  /** Lena's bubble above the question, when the card gives one. */
  readonly help?: string;
  readonly title: string;
  /** Option id → label, for a single or multiple choice. */
  readonly options?: Readonly<Record<string, string>>;
  /** The words under both ends of a scale. */
  readonly scale?: { readonly min: string; readonly max: string };
  /** The unit after a stepper's number ("h"). */
  readonly unit?: string;
  /** The example inside a written field, gone once she types. */
  readonly placeholder?: string;
  /** The second button of a screen answered by its fields (« Não sinto este sintoma »). */
  readonly none?: string;
  /** The part of a long questionnaire the screen belongs to (LEAF-Q, « 1 · Lesões »), above the question. */
  readonly section?: string;
  /** SPEC-28, the recap: the line of a screen answered with its second button (« Não sinto »). */
  readonly recapNone?: string;
  /** SPEC-28, the recap: shorter option labels, as a line lists them (« antes »). */
  readonly short?: Readonly<Record<string, string>>;
  /** SPEC-28, the recap: the word before a field's value (« intensidade 7 / 10 », « Outro: Acne »). */
  readonly recap?: string;
}

/** What one assessment says, from its introduction to the thanks (SPEC-19; SPEC-27 took the invitation away). */
export interface QuestionnaireText {
  readonly intro: { readonly title: string; readonly text: string };
  /** By question id, every screen of the lib's item list and every field drawn under an option. */
  readonly questions: Readonly<Record<string, QuestionText>>;
  /** Lena's bubbles once she taps "Terminar"; `closing` is the lib's name for it, when the item list has several. */
  readonly closing: (firstName: string, closing?: string) => readonly string[];
}

/** "Obrigada, Mariana." or "Obrigada." */
const thanks = (firstName: string) => (firstName ? `Obrigada, ${firstName}.` : "Obrigada.");

// SPEC-19, ICIQ-UI SF. The items in the tu form as the founder rewrote
// them on the card (2026-10-15); PYF's own wording, no source in the code
// (SPEC-12, question 6). Option ids are poweryourflow-lib's ICIQ.
const iciq: QuestionnaireText = {
  intro: {
    title: "Perdas de urina nas últimas quatro semanas.",
    text:
      "Muitas atletas perdem urina de vez em quando. Estas perguntas ajudam a perceber com que frequência te acontece " +
      "e quanto te incomoda. Pensa em como tens andado, em média, nas últimas quatro semanas.",
  },
  questions: {
    frequency: {
      help: "Pensa no que ocorreu, em média, nas últimas quatro semanas.",
      title: "Com que frequência tens perdas de urina?",
      options: {
        nunca: "nunca",
        "semana-1": "cerca de uma vez por semana ou menos",
        "semana-2-3": "duas ou três vezes por semana",
        "dia-1": "cerca de uma vez por dia",
        "dia-varias": "diversas vezes ao dia",
        sempre: "o tempo todo",
      },
    },
    amount: {
      title: "Qual a quantidade habitual da perda de urina (quer uses proteção ou não)?",
      options: {
        nenhuma: "nenhuma",
        pequena: "uma pequena quantidade",
        moderada: "uma quantidade moderada",
        grande: "uma grande quantidade",
      },
    },
    interference: {
      title: "No geral, a perda de urina interfere na tua vida diária?",
      scale: { min: "sem interferência", max: "bastante interferência" },
    },
    when: {
      title: "Quando perdes urina?",
      options: {
        nunca: "nunca",
        "antes-casa-de-banho": "perdes antes de chegar à casa de banho",
        "tosse-espirro": "perdes quando tosses ou espirras",
        "a-dormir": "perdes quando estás a dormir",
        "atividade-fisica": "perdes quando estás em atividade física / exercício",
        "depois-de-urinar": "perdes quando terminaste de urinar e estás a vestir-te",
        "sem-razao": "perdes sem razão óbvia",
        sempre: "perdes o tempo todo",
      },
    },
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o questionário.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

// SPEC-23, Sono (ASBQ). The 18 statements, the stepper and the bedtime as
// the card words them, PYF's own wording with no source in the code
// (SPEC-12, question 6). The introduction is ours, written by BRAND and
// shown to the founder on the card. Option and question ids are
// poweryourflow-lib's SONO.
const sonoFrequency = {
  nunca: "Nunca",
  raramente: "Raramente",
  "as-vezes": "Às vezes",
  frequentemente: "Frequentemente",
  sempre: "Sempre",
};
const sonoHelp = "Nas últimas semanas (no último mês)… Assinala uma resposta.";
const sonoStatement = (title: string): QuestionText => ({ help: sonoHelp, title, options: sonoFrequency });

const sono: QuestionnaireText = {
  intro: {
    title: "Os teus hábitos de sono no último mês.",
    text:
      "São 18 frases sobre o que fazes antes de dormir e durante a noite. No fim, 2 perguntas sobre as tuas horas de sono. " +
      "Pensa em como tens dormido nas últimas semanas. Não há respostas certas ou erradas.",
  },
  questions: {
    sestas: sonoStatement("Faço sestas de 2 horas ou mais à tarde."),
    estimulantes: sonoStatement("Tomo estimulantes quando treino ou compito (por exemplo cafeína, bebidas energéticas)."),
    "treino-tarde": sonoStatement("Treino ou compito tarde à noite (depois das 19h)."),
    alcool: sonoStatement("Bebo álcool nas 4 horas antes de me deitar."),
    "deitar-irregular": sonoStatement("Deito-me a horas diferentes de noite para noite (mais de 1 hora de diferença)."),
    sede: sonoStatement("Deito-me com sede."),
    "dores-musculares": sonoStatement("Deito-me com dores musculares."),
    ecras: sonoStatement("Uso ecrãs na hora antes de me deitar (telemóvel, computador, televisão, consola)."),
    "pensar-desporto": sonoStatement("Na cama, penso, planeio ou preocupo-me com a minha performance desportiva."),
    "pensar-outros": sonoStatement("Na cama, penso, planeio ou preocupo-me com assuntos que não têm a ver com o desporto."),
    comprimidos: sonoStatement("Tomo comprimidos para dormir."),
    "casa-de-banho": sonoStatement("Acordo durante a noite para ir à casa de banho."),
    ressonar: sonoStatement("Acordo-me a mim ou a quem dorme comigo por causa do meu ressonar."),
    espasmos: sonoStatement("Acordo-me a mim ou a quem dorme comigo por causa de espasmos musculares."),
    "levantar-irregular": sonoStatement("Levanto-me a horas diferentes de manhã para manhã (mais de 1 hora de diferença)."),
    ambiente: sonoStatement(
      "Em casa, durmo num ambiente pouco adequado (demasiada luz ou barulho, cama ou almofada desconfortável, demasiado calor ou frio).",
    ),
    "fora-de-casa": sonoStatement("Durmo em sítios que não são a minha casa (hotéis, estágios, casa de outras pessoas)."),
    viagens: sonoStatement("As viagens impedem-me de ter horários de sono regulares."),
    horas: { help: "Numa noite normal.", title: "Quantas horas dormes, em média, numa noite normal?", unit: "h" },
    "deitar-competicao": {
      help: "Na véspera de um jogo ou competição.",
      title: "A que horas te deitas na véspera de um jogo ou competição?",
    },
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o questionário.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

// SPEC-21, Ciclo e desporto. The questions and options as the card writes
// them; the introduction is ours (the card gives none), written to BRAND
// §4 "Cycle" and §8 "Question sensible — intro", and copied on the card's
// Dev notes. The field's example names no medicine (founder, 2026-10-15).
// Option ids are poweryourflow-lib's CICLO_DESPORTO.
const phases = {
  inicio: "No início",
  meio: "A meio",
  fim: "No fim",
  "sem-diferenca": "Não noto diferença",
  "nao-sei": "Não sei",
};

const cicloDesporto: QuestionnaireText = {
  intro: {
    title: "O período e o desporto.",
    text:
      "O ciclo não é uma fraqueza. É um dado de treino. As próximas perguntas são sobre o teu período e os teus treinos. " +
      "Não há respostas certas ou erradas, e ninguém vê as tuas respostas sem autorização.",
  },
  questions: {
    discomfort: {
      title: "O teu ciclo incomoda-te nos treinos?",
      options: { nada: "Nada", "um-pouco": "Um pouco", "mais-ou-menos": "Mais ou menos", muito: "Muito" },
    },
    missed: {
      title: "Nos últimos 12 meses, faltaste a algum treino ou competição por causa do período?",
      options: { nunca: "Nunca", "1-2": "1 ou 2 vezes", "3-mais": "3 vezes ou mais" },
    },
    best: { title: "Em que altura do ciclo te sentes com melhor rendimento?", options: phases },
    worst: { title: "E com pior rendimento?", options: phases },
    relief: {
      title: "O que fazes quando o período te incomoda?",
      options: {
        nada: "Nada",
        analgesicos: "Tomo analgésicos ou anti-inflamatórios",
        calor: "Uso calor",
        "adapto-treino": "Adapto o treino",
        descanso: "Descanso",
        outra: "Outra coisa",
      },
    },
    medication: { title: "Qual e quantos dias?", placeholder: "Ex.: nome do medicamento, 2 dias" },
    works: { title: "Isso resulta?", options: { sim: "Sim", "em-parte": "Em parte", nao: "Não" } },
  },
  closing: (firstName) => [
    `${thanks(firstName)} Ficou registado.`,
    "Vou juntar estas respostas aos teus sintomas para perceber melhor o teu ciclo.",
  ],
};

// SPEC-20, Ciclo · sintomas. The card's texts, introduction included;
// PYF's own wording, no source in the code (SPEC-12, question 6). Question
// and option ids are poweryourflow-lib's CICLO_SINTOMAS: each symptom's
// screen, its « quando » and its « intensidade », and Outro's « Qual? ».
const moments = { antes: "Antes do período", durante: "Durante o período", meio: "A meio do ciclo" };
const intensity = { min: "nada", max: "o pior possível" };

/** How the recap lists a moment (SPEC-28): « antes · durante ». */
const momentsShort = { antes: "antes", durante: "durante", meio: "a meio do ciclo" };

/** One symptom's screen and its two blocks; n is its place in the card's order. */
const symptom = (id: string, n: number, title: string, none = "Não sinto este sintoma", recapNone = "Não sinto"): Record<string, QuestionText> => ({
  [id]: { help: `Sintoma ${n} de 12.`, title, none, recapNone },
  [`${id}-quando`]: { title: "Quando", options: moments, short: momentsShort },
  [`${id}-intensidade`]: { title: "Intensidade", scale: intensity, recap: "intensidade" },
});

const cicloSintomas: QuestionnaireText = {
  intro: {
    title: "Um sintoma de cada vez",
    text: "Passamos pelos 12. Para cada um, dizes quando aparece e com que intensidade, ou carregas em «Não sinto».",
  },
  questions: {
    ...symptom("colicas", 1, "Cólicas, dor na parte de baixo da barriga"),
    ...symptom("lombar", 2, "Dor lombar"),
    ...symptom("cabeca", 3, "Dor de cabeça, enxaqueca"),
    ...symptom("cansaco", 4, "Cansaço"),
    ...symptom("digestao", 5, "Barriga inchada, problemas de digestão"),
    ...symptom("mamas", 6, "Mamas sensíveis ou doridas"),
    ...symptom("humor", 7, "Humor: irritação, tristeza, ansiedade"),
    ...symptom("dormir", 8, "Dormir mal"),
    ...symptom("inchaco", 9, "Sensação de inchaço, pernas pesadas"),
    ...symptom("apetite", 10, "Vontade de comer fora de horas"),
    ...symptom("coordenacao", 11, "Menos coordenação ou precisão"),
    ...symptom("outro", 12, "Sentes outro sintoma?", "Não tenho outro sintoma", "Não tenho outro sintoma"),
    "outro-qual": { title: "Qual?", recap: "Outro:" },
  },
  closing: (firstName, closing) =>
    closing === "sem-sintomas"
      ? [`${thanks(firstName)} Registei que não sentes sintomas ao longo do ciclo.`]
      : [
          `${thanks(firstName)} Ficou registado.`,
          "Vou ter estes sintomas em conta quando olhar para o teu ciclo e para a carga de treino.",
        ],
};

// SPEC-24, BEDA-Q. The card's texts; the introduction is ours, written
// for the card's Dev notes. PYF's own wording, no source in the code
// (SPEC-12, question 6). Neutral on a sensitive subject: nothing here reads
// an answer back to her. Option ids are poweryourflow-lib's BEDA.
const bedaFrequency = {
  help: "Nas últimas 2 semanas, com que frequência isto se aplicou a ti?",
  options: { "1": "Sempre", "2": "Quase sempre", "3": "Muitas vezes", "4": "Às vezes", "5": "Raramente", "6": "Nunca" },
} as const;
const bedaYesNo = { sim: "Sim", nao: "Não" } as const;

const beda: QuestionnaireText = {
  intro: {
    title: "A forma como comes e vês o teu corpo.",
    text:
      "Estas perguntas são sobre a tua relação com a comida, com o teu corpo e com o peso. " +
      "Não há respostas certas ou erradas. Responde com o que é verdade para ti.",
  },
  questions: {
    guilt: { ...bedaFrequency, title: "Sinto-me extremamente culpada depois de comer demais." },
    thinner: { ...bedaFrequency, title: "Ando preocupada com a vontade de ser mais magra." },
    belly: { ...bedaFrequency, title: "Acho que a minha barriga é demasiado grande." },
    "body-shape": { ...bedaFrequency, title: "Sinto-me satisfeita com a forma do meu corpo." },
    "parents-expectations": { ...bedaFrequency, title: "Os meus pais sempre esperaram que eu fosse excelente." },
    "not-disappoint": {
      ...bedaFrequency,
      title: "Quando era criança, esforçava-me muito para não desiludir os meus pais e professores.",
    },
    "losing-weight-now": { help: "Responde sim ou não.", title: "Estás a tentar perder peso neste momento?", options: bedaYesNo },
    "tried-losing-weight": { title: "Já tentaste perder peso?", options: bedaYesNo },
    attempts: {
      help: "Uma resposta.",
      title: "Se sim, quantas vezes tentaste perder peso?",
      options: { "1-2": "1 a 2 vezes", "3-5": "3 a 5 vezes", "mais-de-5": "mais de 5 vezes" },
    },
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o questionário.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

// SPEC-36, CISS. The 15 statements and the help as the card writes them,
// PYF's own wording in the tu form with no source in the code (SPEC-12,
// question 6); the introduction is ours, on the card. Option and question
// ids are poweryourflow-lib's CISS; the points are the lib's and never show.
const cissFrequency = {
  nunca: "Nunca",
  raramente: "Raramente",
  "as-vezes": "Às vezes",
  frequentemente: "Frequentemente",
  sempre: "Sempre",
};
const cissHelp = "Quando lês, estudas ou usas o telemóvel ou o computador durante algum tempo:";
const cissStatement = (title: string): QuestionText => ({ help: cissHelp, title, options: cissFrequency });

const ciss: QuestionnaireText = {
  intro: {
    title: "Os teus olhos quando lês ou usas ecrãs.",
    text:
      "São 15 perguntas sobre o que sentes quando lês, estudas ou usas o telemóvel ou o computador durante algum tempo. " +
      "Não há respostas certas ou erradas.",
  },
  questions: {
    "olhos-cansados": cissStatement("Sentes os olhos cansados?"),
    desconforto: cissStatement("Sentes desconforto nos olhos?"),
    "dores-de-cabeca": cissStatement("Ficas com dores de cabeça?"),
    sonolencia: cissStatement("Ficas com sono?"),
    concentracao: cissStatement("Perdes a concentração?"),
    lembrar: cissStatement("Tens dificuldade em lembrar-te do que leste?"),
    "ver-a-dobrar": cissStatement("Vês a dobrar?"),
    "palavras-mexem": cissStatement("Vês as palavras a mexer, a saltar ou a flutuar na página?"),
    "ler-devagar": cissStatement("Achas que lês devagar?"),
    "olhos-doem": cissStatement("Doem-te os olhos?"),
    "olhos-irritados": cissStatement("Sentes os olhos doridos ou irritados?"),
    "puxar-olhos": cissStatement("Sentes como se algo puxasse à volta dos olhos?"),
    desfocado: cissStatement("As palavras ficam desfocadas, ou focam e desfocam?"),
    "perder-sitio": cissStatement("Perdes o sítio onde ias no texto?"),
    "reler-linha": cissStatement("Tens de reler a mesma linha?"),
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o questionário.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

// SPEC-37, Fonseca. The 10 questions as the card writes them, PYF's own
// wording in the tu form with no source in the code (SPEC-12, question 6);
// Q7 names the joint, which the form's « nessa articulação » left to Q6's
// screen (the founder's answer 4 on SPEC-35). No help line: the
// introduction says how to answer. Option and question ids are
// poweryourflow-lib's FONSECA; the points are the lib's and never show.
const fonsecaAnswers = { sim: "Sim", "as-vezes": "Às vezes", nao: "Não" };
const fonsecaQuestion = (title: string): QuestionText => ({ title, options: fonsecaAnswers });

const fonseca: QuestionnaireText = {
  intro: {
    title: "O teu maxilar e a mastigação.",
    text:
      "São 10 perguntas sobre o teu maxilar, a mastigação, algumas dores e alguns hábitos. " +
      "Responde a cada uma com Sim, Às vezes ou Não. Não há respostas certas ou erradas.",
  },
  questions: {
    "abrir-boca": fonsecaQuestion("Tens dificuldade em abrir a boca?"),
    "maxilar-lados": fonsecaQuestion("Tens dificuldade em mexer o maxilar para os lados?"),
    mastigar: fonsecaQuestion("Sentes cansaço ou dor nos músculos quando mastigas?"),
    "dores-de-cabeca": fonsecaQuestion("Tens dores de cabeça com frequência?"),
    nuca: fonsecaQuestion("Tens dores na nuca ou torcicolos?"),
    ouvido: fonsecaQuestion("Tens dores de ouvido ou na zona da articulação do maxilar, à frente da orelha?"),
    estalidos: fonsecaQuestion("Notas estalidos ou ruídos na articulação do maxilar quando mastigas ou abres a boca?"),
    habitos: fonsecaQuestion(
      "Tens o hábito de apertar ou ranger os dentes, mascar pastilha, roer as unhas, morder os lábios ou a caneta?",
    ),
    dentes: fonsecaQuestion("Sentes que os teus dentes não encaixam bem uns nos outros?"),
    tensa: fonsecaQuestion("Consideras-te uma pessoa tensa ou nervosa?"),
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o questionário.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

// SPEC-22, LEAF-Q. The questions, options and fields as the card writes
// them, PYF's own wording in the tu form with no source in the code
// (SPEC-12, question 6); the introduction is ours (the card gives none),
// written to BRAND §8 "Question sensible — intro" and copied on the card's
// Dev notes. Each screen carries its section's label. Option and question
// ids are poweryourflow-lib's LEAFQ.
const leafqYesNo = { sim: "Sim", nao: "Não" };
const leafqGut = {
  "varias-dia": "Sim, várias vezes por dia",
  "varias-semana": "Sim, várias vezes por semana",
  "1-2-semana": "Sim, 1 ou 2 vezes por semana ou menos",
  raramente: "Raramente ou nunca",
};
const leafqSince = {
  "1-2-meses": "Há 1 a 2 meses",
  "3-4-meses": "Há 3 a 4 meses",
  "5-6-meses": "Há 5 a 6 meses",
  "mais-6-meses": "Há mais de 6 meses",
  "12-meses-ou-mais": "Há 12 meses ou mais",
};
const leafqOther = { title: "Outro: qual?" };
const inSection = (section: string, questions: Record<string, QuestionText>): Record<string, QuestionText> =>
  Object.fromEntries(Object.entries(questions).map(([id, said]) => [id, { ...said, section }]));

const leafq: QuestionnaireText = {
  intro: {
    title: "Lesões, digestão e ciclo.",
    text:
      "São 4 partes: lesões, função intestinal, contraceção e menstruação. Algumas perguntas só aparecem conforme as tuas respostas. " +
      "Não há respostas certas ou erradas, e ninguém vê as tuas respostas sem autorização. " +
      "Se saíres a meio, o que já respondeste fica guardado.",
  },
  questions: {
    ...inSection("1 · Lesões", {
      injuries: {
        title: "No último ano, faltaste a treinos ou competições por causa de lesões?",
        options: { nao: "Não, nunca", "1-2": "Sim, 1 ou 2 vezes", "3-4": "Sim, 3 ou 4 vezes", "5-mais": "Sim, 5 vezes ou mais" },
      },
      "days-missed": {
        title: "Se sim, quantos dias faltaste no total, no último ano?",
        options: { "1-7": "1 a 7 dias", "8-14": "8 a 14 dias", "15-21": "15 a 21 dias", "22-mais": "22 dias ou mais" },
      },
      "stress-fracture": {
        title: "Se sim, tiveste alguma fratura de stress (lesão do osso por sobrecarga)?",
        options: leafqYesNo,
      },
      "overuse-injuries": {
        title: "Se sim, tiveste outras lesões por sobrecarga (tendinite, dor que foi aparecendo aos poucos)?",
        options: leafqYesNo,
      },
      "acute-injuries": { title: "Se sim, tiveste alguma lesão aguda (entorse, rotura, pancada)?", options: leafqYesNo },
    }),
    "stress-fracture-count": { title: "Quantas?" },
    "stress-fracture-where": {
      title: "Onde?",
      options: {
        pe: "Pé",
        tornozelo: "Tornozelo",
        perna: "Perna (tíbia)",
        joelho: "Joelho",
        anca: "Anca / pélvis",
        costas: "Costas",
        outro: "Outro",
      },
    },
    "overuse-injuries-detail": { title: "Quantas e onde?", placeholder: "Ex.: 2, tendão de Aquiles e joelho" },
    "acute-injuries-detail": { title: "Quantas e onde?", placeholder: "Ex.: 1, entorse no tornozelo" },
    ...inSection("2 · Função intestinal", {
      bloating: { title: "Sentes gases ou a barriga inchada, mesmo quando não estás com o período?", options: leafqGut },
      cramps: { title: "Tens cólicas ou dores de barriga que não têm a ver com o período?", options: leafqGut },
      "bowel-frequency": {
        title: "Com que frequência evacuas (fazes cocó), em média?",
        options: {
          "varias-dia": "Várias vezes por dia",
          "1-dia": "Uma vez por dia",
          "dia-sim-dia-nao": "Dia sim, dia não",
          "2-semana": "Duas vezes por semana",
          "1-semana-ou-menos": "Uma vez por semana ou menos",
        },
      },
      stool: {
        title: "Como são normalmente as tuas fezes?",
        options: { normais: "Normais (moles)", diarreia: "Tipo diarreia (líquidas)", duras: "Duras e secas" },
      },
      "bowel-comments": { title: "Comentários" },
    }),
    ...inSection("3.1 · Contraceção", {
      pill: { title: "Tomas a pílula?", options: leafqYesNo },
      "pill-why": {
        title: "Se sim, porquê?",
        options: {
          contracecao: "Contraceção",
          "menos-dores": "Para ter menos dores",
          "sangrar-menos": "Para sangrar menos",
          competicoes: "Para ajustar o ciclo às competições",
          "periodo-para": "Porque sem ela o período para",
          outro: "Outro",
        },
      },
      "pill-before": { title: "Se não, já tomaste a pílula antes?", options: leafqYesNo },
      "other-hormonal": { title: "Usas outro contracetivo hormonal?", options: leafqYesNo },
      "other-hormonal-which": {
        title: "Se sim, qual?",
        options: { anel: "Anel vaginal", diu: "DIU hormonal", implante: "Implante", adesivo: "Adesivo", outro: "Outro" },
      },
    }),
    "pill-why-other": leafqOther,
    "pill-before-detail": { title: "Quando e durante quanto tempo?" },
    "other-hormonal-other": leafqOther,
    ...inSection("3.2 · Menstruação", {
      menarche: {
        help: "Se nunca tiveste, o questionário termina aqui.",
        title: "Que idade tinhas quando tiveste o primeiro período?",
        options: {
          "11-ou-menos": "11 anos ou menos",
          "12-14": "12 a 14 anos",
          "15-ou-mais": "15 anos ou mais",
          "nao-lembro": "Não me lembro",
          nunca: "Nunca tive o período",
        },
      },
      "menarche-natural": {
        title: "O primeiro período veio naturalmente (sozinho)?",
        options: { sim: "Sim", nao: "Não", "nao-lembro": "Não me lembro" },
      },
      "menarche-help": {
        title: "Se não, o que foi feito para ele aparecer?",
        options: { hormonal: "Tratamento hormonal", peso: "Aumento de peso", "menos-treino": "Menos treino", outro: "Outro" },
      },
      "normal-periods": { title: "Tens menstruações normais?", options: { sim: "Sim", nao: "Não", "nao-sei": "Não sei" } },
      "last-period": {
        title: "Se sim, quando foi o teu último período?",
        options: { "0-4-semanas": "Há 0 a 4 semanas", ...leafqSince },
      },
      "regular-cycle": {
        title: "Se sim, os teus períodos são regulares (de 28 em 28 a 34 em 34 dias)?",
        options: { "quase-sempre": "Sim, quase sempre", "quase-nunca": "Não, quase nunca" },
      },
      "bleeding-days": {
        title: "Se sim, quantos dias sangras normalmente?",
        options: { "1-2": "1 a 2", "3-4": "3 a 4", "5-6": "5 a 6", "7-8": "7 a 8", "9-mais": "9 ou mais" },
      },
      "heavy-periods": { title: "Se sim, já tiveste problemas com menstruações muito abundantes?", options: leafqYesNo },
      "periods-last-year": {
        title: "Se sim, quantos períodos tiveste no último ano?",
        options: { "12-mais": "12 ou mais", "9-11": "9 a 11", "6-8": "6 a 8", "3-5": "3 a 5", "0-2": "0 a 2" },
      },
      "last-period-other": {
        title: "Se respondeste Não ou Não sei à pergunta anterior, quando foi o teu último período?",
        options: { ...leafqSince, gravida: "Estou grávida" },
      },
      amenorrhea: {
        title: "Alguma vez o teu período parou durante 3 meses seguidos ou mais (sem estares grávida)?",
        options: { nunca: "Não, nunca", aconteceu: "Sim, já aconteceu", agora: "Sim, está a acontecer agora" },
      },
      "training-change": {
        title: "Sentes que a tua menstruação muda quando treinas mais (mais intensidade, mais vezes ou mais tempo)?",
        options: leafqYesNo,
      },
      "training-change-how": {
        title: "Se sim, como?",
        options: {
          "sangro-menos": "Sangro menos",
          "menos-dias": "Sangro menos dias",
          para: "O período para",
          "sangro-mais": "Sangro mais",
          "mais-dias": "Sangro mais dias",
        },
      },
    }),
    "menarche-help-other": leafqOther,
  },
  closing: (firstName) => [
    `${thanks(firstName)} Terminaste o LEAF-Q.`,
    "As tuas respostas ficam guardadas de forma confidencial no teu perfil de saúde.",
  ],
};

const questionnaires: Partial<Record<AssessmentId, QuestionnaireText>> = {
  iciq,
  "ciclo-sintomas": cicloSintomas,
  "ciclo-desporto": cicloDesporto,
  leafq,
  sono,
  beda,
  ciss,
  fonseca,
};

/** The assistant who speaks in every conversation. */
const ASSISTANT = "Lena";

/** What a run left mid-way still asks of her, in Conversa's resume. */
function leftToDo(left: number, unit: { one: string; many: string }): string {
  // SPEC-28: every question answered, the recap still to confirm.
  if (left === 0) return "Só falta confirmares as respostas. Continuamos?";
  if (left === 1) return `Falta 1 ${unit.one}. Continuamos?`;
  return `Faltam ${left} ${unit.many}. Continuamos?`;
}

export const copy = {
  brand: {
    name: "PYF",
    tagline: "Power your flow",
  },
  // SPEC-31: the segments of the language control, and each language's name
  // in its own words, for a screen reader; the same in every language.
  languages: {
    pt: { short: "PT", name: "Português" },
    fr: { short: "FR", name: "Français" },
    en: { short: "EN", name: "English" },
  },
  // The founder, 2026-10-15: the assistant who speaks in every conversation
  // is Lena; the product, its logo and its texts about the service stay PYF.
  assistant: {
    name: ASSISTANT,
  },
  // SPEC-27: the navigation pill on top of the three tabs.
  nav: {
    label: "Navegação",
    conversa: "Conversa",
    assessments: "Avaliações",
    profile: "Perfil",
    openProfile: "Abrir o teu perfil",
  },
  // SPEC-27, Action list and the founder's answers of 2026-10-19 (Lena
  // speaks, what is left in the hub row's unit). Dados gerais counts 8
  // since SPEC-55: the count comes from the lib, never from these words.
  // Which case, the counts and the greeting are poweryourflow-lib's
  // (conversaProposal, greetingAt).
  conversa: {
    loading: "A preparar a conversa",
    failed: "Algo correu mal do nosso lado. As tuas respostas estão guardadas. Tenta outra vez.",
    retry: "Tentar outra vez",
    greetings: { morning: "Bom dia", afternoon: "Boa tarde", evening: "Boa noite" },
    /** "Bom dia, Mariana." or "Bom dia." */
    hello: (greeting: string, firstName: string) => (firstName ? `${greeting}, ${firstName}.` : `${greeting}.`),
    welcome: (count: number, minutes: number) => [
      `Olá. Sou a ${ASSISTANT}, vou acompanhar-te nos treinos.`,
      `Antes de começar, preciso de te conhecer. São ${count} perguntas, ${minutes} minutos.`,
    ],
    resume: (hello: string, left: number, unit: { one: string; many: string }) => [
      `${hello} Deixaste uma avaliação a meio.`,
      leftToDo(left, unit),
    ],
    next: (hello: string, remaining: number, minutes: number) => [
      remaining === 1
        ? `${hello} Falta 1 avaliação para completar o teu perfil de saúde.`
        : `${hello} Faltam ${remaining} avaliações para completar o teu perfil de saúde.`,
      `A próxima demora ${minutes} ${minutes === 1 ? "minuto" : "minutos"}. Fazemos agora?`,
    ],
    allDone: (hello: string) => [
      `${hello} Já respondeste a todas as avaliações.`,
      "Não há nada a fazer por agora. Quando houver uma nova, digo-te aqui.",
    ],
    /**
     * SPEC-28 and SPEC-31: Lena's thanks here after « Confirmar respostas ». In PT,
     * the questionnaire's own closing words; another language names the
     * assessment by its hub title, as the card writes it in French.
     */
    thanks: (firstName: string, _title: string, assessmentId: AssessmentId, closing?: string): readonly string[] =>
      questionnaires[assessmentId]?.closing(firstName, closing) ?? [],
    units: {
      questions: { one: "pergunta", many: "perguntas" },
      symptoms: { one: "sintoma", many: "sintomas" },
      parts: { one: "parte", many: "partes" },
    },
    todoKicker: (kicker: string) => `Avaliação · ${kicker}`,
    todoMeta: (count: number, unit: string, minutes: number) => `${count} ${unit} · ${minutes} min · confidencial`,
    progressKicker: (kicker: string) => `Em curso · ${kicker}`,
    progressMeta: (answered: number, total: number) => `${answered} / ${total} respondidas · continua onde ficaste`,
    start: "Começar",
    resumeButton: "Continuar",
    open: "Abrir a avaliação",
    doneTitle: "Tudo em dia",
    doneLine: (done: number) => `${done} avaliações feitas`,
    doneCount: (done: number, total: number) => `${done} / ${total}`,
    toAssessments: "Ver as minhas avaliações",
    // Visible and out of scope: nothing is sent in this version.
    composer: `Escreve à ${ASSISTANT}…`,
    send: "Enviar",
  },
  splash: {
    loading: "A abrir a PYF",
  },
  welcome: {
    // SPEC-13, Action list: the exact texts.
    title: "Treina mais, com menos dor.",
    lead: "A PYF acompanha a dor, a carga de treino e o ciclo, e sugere o próximo passo.",
    signUp: "Criar conta",
    signIn: "Já tenho conta · Entrar",
    /** SPEC-31: the name of the PT · FR · EN control over the photo. */
    language: "Idioma",
  },
  // SPEC-14, Action list: the exact texts, and the error for a name too long.
  signUp: {
    back: "Voltar",
    signIn: "Entrar",
    title: "Cria a tua conta.",
    lead: "Demora menos de um minuto.",
    firstNameLabel: "Como te chamas?",
    firstNamePlaceholder: "Primeiro nome",
    emailLabel: "Email",
    emailPlaceholder: "nome@email.com",
    passwordLabel: "Palavra-passe",
    passwordPlaceholder: "Mínimo 8 caracteres",
    passwordHint: "8 caracteres · 1 número",
    submit: "Continuar",
    submitting: "A criar…",
    errors: {
      firstNameEmpty: "Diz-me como te chamas.",
      firstNameTooLong: "Usa no máximo 40 caracteres.",
      emailInvalid: "Confirma o email.",
      emailInUse: "Já existe uma conta com este email.",
      passwordInvalid: "Precisa de 8 caracteres e 1 número.",
      failed: "Não consegui criar a conta. Tenta de novo.",
    },
    // "Ao continuar aceitas os Termos e a Política de privacidade. Os teus
    // dados de saúde ficam só contigo.", cut around its two links.
    legal: {
      before: "Ao continuar aceitas os ",
      terms: "Termos",
      between: " e a ",
      privacy: "Política de privacidade",
      after: ". Os teus dados de saúde ficam só contigo.",
    },
  },
  // SPEC-15, Action list: the exact texts, and the messages the card leaves
  // open (Google's email already used with a password, a reset that fails).
  signIn: {
    back: "Voltar",
    signUp: "Criar conta",
    title: "Bem-vinda de volta.",
    lead: "Entra para continuar onde ficaste.",
    emailLabel: "Email",
    emailPlaceholder: "nome@email.com",
    passwordLabel: "Palavra-passe",
    forgot: "Esqueci-me da palavra-passe",
    // "Enviámos um email para {email}.", cut around the address.
    resetSent: { before: "Enviámos um email para ", after: "." },
    submit: "Entrar",
    submitting: "A entrar…",
    or: "ou",
    google: "Continuar com Google",
    errors: {
      wrongCredentials: "Palavra-passe incorreta. Tenta de novo.",
      tooManyAttempts: "Demasiadas tentativas. Tenta daqui a 10 minutos.",
      offline: "Sem ligação. Tenta de novo.",
      emailFirst: "Escreve o teu email primeiro.",
      accountExists: "Já tens conta com este email. Entra com a palavra-passe.",
      resetFailed: "Não consegui enviar o email. Tenta de novo.",
      failed: "Não consegui entrar. Tenta de novo.",
    },
  },
  // SPEC-15: the screen the reset email's link opens. Same rule as Criar conta.
  newPassword: {
    back: "Voltar",
    title: "Escolhe uma nova palavra-passe.",
    lead: "Depois, entras com ela.",
    label: "Nova palavra-passe",
    placeholder: "Mínimo 8 caracteres",
    hint: "8 caracteres · 1 número",
    submit: "Guardar",
    submitting: "A guardar…",
    doneTitle: "Palavra-passe guardada.",
    doneLead: "Já podes entrar com a nova palavra-passe.",
    invalidTitle: "Este link já não serve.",
    invalidLead: "Cada link dura 1 hora e só serve uma vez. Pede outro no ecrã Entrar.",
    signIn: "Entrar",
    errors: {
      passwordInvalid: "Precisa de 8 caracteres e 1 número.",
      offline: "Sem ligação. Tenta de novo.",
      failed: "Não consegui guardar a palavra-passe. Tenta de novo.",
    },
  },
  legal,
  // SPEC-18, Action list: the exact texts. The stop under the minimum age
  // (SPEC-12, question 4) and the save failure are ours; the card's Dev
  // notes carry them for the founder.
  profileOnboarding: {
    back: "Voltar",
    part: "Parte 1 · Dados gerais",
    typing: "A Lena está a escrever",
    send: "Enviar",
    // Before the first question she is asked: the birth date on a first
    // run, whose first name came from Criar conta; the name in an edit.
    greeting: "Olá. Sou a Lena, vou acompanhar-te nos treinos.",
    firstName: ["Antes de começar, preciso de te conhecer. Como te chamas?"],
    firstNamePlaceholder: "O teu primeiro nome",
    birthDate: (name: string) => [`Prazer, ${name}. Qual é a tua data de nascimento?`],
    birthDateAgain: "Qual é a tua data de nascimento?",
    birthDatePlaceholder: "dd/mm/aaaa",
    dateUnreadable: "Não consegui ler a data. Escreve assim: 14/03/2001.",
    tooYoung: (name: string, minAge: number) => [
      `Obrigada, ${name}. A PYF é para atletas a partir dos ${minAge} anos.`,
      `Por isso, ficamos por aqui. Não guardei a tua data de nascimento. Quando fizeres ${minAge} anos, estou cá à tua espera.`,
    ],
    leave: "Sair",
    modality: (age: number) => [`Tens ${age} anos.`, "Que modalidade praticas?"],
    // BRAND.md §2, the priority sports (the founder's answer on SPEC-18).
    modalityChips: ["Futebol", "Futsal", "Andebol", "Ginástica", "Natação", "Atletismo", "Judo", "Voleibol"],
    modalityOther: "Outra",
    modalityOtherPlaceholder: "Escreve a tua modalidade",
    orWriteHere: "Ou escreve aqui…",
    position: ["Qual é a tua posição, disciplina ou categoria de peso?"],
    positionPlaceholder: "Ex.: médio, 400 m, −60 kg",
    level: ["A que nível competes?"],
    levelChips: ["Regional", "Nacional", "Internacional"],
    levelOther: "Outro",
    levelOtherPlaceholder: "Escreve o teu nível",
    club: ["Em que clube treinas?"],
    noClub: "Sem clube",
    clubPlaceholder: "Nome do clube",
    load: ["Quantos treinos fazes por semana, e quantas horas no total?"],
    trainings: "Treinos por semana",
    hours: "Horas por semana",
    less: "Menos",
    more: "Mais",
    confirm: "Confirmar",
    loadAnswer: (trainings: number, hours: number) =>
      `${trainings} ${trainings === 1 ? "treino" : "treinos"} · ${hours} h por semana`,
    competition: ["Como é a tua competição?"],
    competitionOptions: {
      league: { letter: "A", title: "Jogos regulares num campeonato", hint: "Desportos coletivos" },
      events: { letter: "B", title: "Provas ou torneios ao longo da época", hint: "Desportos individuais ou de combate" },
    },
    closing: (name: string) => [
      `Obrigada, ${name}. A parte 1 está feita.`,
      "Aqui fica o resumo. Podes corrigir qualquer dado mais tarde no teu perfil.",
    ],
    saveFailed: "Não consegui guardar os teus dados. Verifica a ligação e tenta outra vez.",
    retry: "Tentar outra vez",
    summary: {
      title: "Parte 1 concluída",
      name: "Nome",
      birth: "Nascimento",
      birthValue: (date: string, age: number) => `${date} · ${age} anos`,
      modality: "Modalidade",
      position: "Posição",
      /** SPEC-55: her side, between the position and the level. */
      laterality: "Lateralidade",
      level: "Nível",
      club: "Clube",
      load: "Carga",
      competition: "Competição",
    },
    toAssessments: "Ver as minhas avaliações",
    toConversa: "Voltar à conversa",
    // Ours (SPEC-17): an edit opened from Perfil ends back there.
    toProfile: "Voltar ao perfil",
    // Ours: the header ← of that edit, named apart from the button above
    // so a screen reader tells the two apart.
    leaveEdit: "Sair da edição",
  },
  // SPEC-16, Action list: the exact texts. The numbers in them come from
  // poweryourflow-lib (ASSESSMENT_FACTS, hubSummary).
  assessments: {
    today: (weekday: string, day: number, month: string) => `Hoje · ${weekday} ${day} ${month}`,
    // Sunday first, as Date.getDay() counts.
    weekdays: ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
    months: ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"],
    title: "As tuas avaliações.",
    loading: "A carregar as tuas avaliações",
    offline: "Sem ligação",
    failed: "Algo correu mal do nosso lado. As tuas respostas estão guardadas. Tenta outra vez.",
    retry: "Tentar outra vez",
    completeProfile: "Completa os teus dados gerais",
    profileLine: (age: number, modality: string, position: string) => `${age} anos · ${modality} · ${position}`,
    doneLabel: "Concluídas",
    left: (remaining: number, minutes: number) =>
      remaining === 1
        ? `Falta 1 avaliação · cerca de ${minutes} min`
        : `Faltam ${remaining} avaliações · cerca de ${minutes} min`,
    allDone: "Todas as avaliações feitas.",
    groups: { progress: "Em curso", todo: "Por fazer", done: "Concluídas" },
    tagTodo: "Por fazer",
    tagDone: "✓ Feito",
    tagProgress: (answered: number, total: number) => `${answered} / ${total}`,
    units: { questions: "perguntas", symptoms: "sintomas", parts: "partes" },
    todoMeta: (kicker: string, count: number, unit: string, minutes: number) => `${kicker} · ${count} ${unit} · ${minutes} min`,
    progressMeta: (kicker: string) => `${kicker} · em curso`,
    /** SPEC-28: a finished assessment reads only; Dados gerais stays editable from here. */
    doneMeta: (kicker: string, day: number, month: string, editable: boolean) =>
      `${kicker} · feito a ${day} ${month} · ${editable ? "editável" : "só leitura"}`,
    // The row of each assessment, in the hub's canonical order.
    rows: {
      dados: { letter: "P", title: "Dados gerais", kicker: "Perfil" },
      iciq: { letter: "I", title: "Perdas de urina", kicker: "ICIQ-UI SF" },
      "ciclo-sintomas": { letter: "C", title: "O teu período: sintomas", kicker: "Ciclo menstrual" },
      "ciclo-desporto": { letter: "C", title: "O período e o desporto", kicker: "Ciclo menstrual" },
      leafq: { letter: "L", title: "Lesões, digestão e ciclo", kicker: "LEAF-Q" },
      sono: { letter: "S", title: "Sono", kicker: "ASBQ" },
      beda: { letter: "B", title: "Alimentação e imagem do corpo", kicker: "BEDA-Q" },
      ciss: { letter: "O", title: "Olhos e visão", kicker: "CISS" },
      fonseca: { letter: "M", title: "Maxilar e mastigação", kicker: "Fonseca" },
    },
  },
  // SPEC-17, Action list: the exact texts, and the unsent-answers sentence
  // the founder kept on 2026-10-15. The waiting, the failures and the
  // screen-reader labels are ours; the card's Dev notes carry them.
  profile: {
    title: "Perfil",
    loading: "A carregar o teu perfil",
    generalData: "Dados gerais",
    edit: "Editar",
    answer: "Responder",
    notAnswered: "Ainda não respondeste.",
    rows: {
      birth: "Data de nascimento",
      modality: "Modalidade",
      position: "Posição",
      /** SPEC-55: her side, between the position and the level. */
      laterality: "Lateralidade",
      level: "Nível",
      club: "Clube",
      load: "Carga",
      competition: "Competição",
    },
    /** SPEC-55: a row she has not answered yet (Lateralidade, for an account older than the question). */
    empty: "—",
    privacyTitle: "Os teus dados de saúde são confidenciais",
    privacyText: "Não são partilhados com o clube nem com o treinador.",
    signOut: "Terminar sessão",
    sending: "A enviar as tuas respostas…",
    confirmTitle: "Terminar sessão?",
    unsent: "Há respostas que ainda não foram enviadas. Se terminares a sessão agora, perdes essas respostas.",
    confirm: "Terminar",
    cancel: "Cancelar",
    leaving: "A terminar…",
    signOutFailed: "Não consegui terminar a sessão. Tenta outra vez.",
    // SPEC-31: the card after Dados gerais. The title is set in capitals by the screen.
    language: {
      title: "Idioma",
      line: "Aplica-se a toda a app.",
      saveFailed: "Não consegui guardar o idioma na tua conta. Fica neste telemóvel e tento outra vez.",
    },
  },
  // SPEC-31: above the Termos and the Política in a language other than PT,
  // which they are not written in yet. Never shown in PT.
  legalNotice: {
    onlyPortuguese: "Este texto só existe em português por agora.",
  },
  // The one-question-per-screen engine (SPEC-19), shared by every
  // assessment after Dados gerais; each one's own words are below it.
  questionnaire: {
    back: "Voltar",
    previousScreen: "Pergunta anterior",
    exit: "Sair",
    previous: "Anterior",
    next: "Continuar",
    finish: "Terminar",
    start: "Começar",
    typing: "A Lena está a escrever",
    today: (time: string) => `Hoje · ${time}`,
    counter: (position: number, total: number) => `${position} / ${total}`,
    progress: "Progresso do questionário",
    optionalField: "(Opcional)",
    hints: {
      single: "(Escolhe uma opção)",
      multi: "(Escolhe uma ou mais opções)",
      number: "(Toca em − ou + para ajustar)",
      time: "(Escreve a hora e os minutos)",
      text: (max: number) => `(Até ${max} caracteres)`,
    },
    /** A stepper or a scale no one has touched yet: no number chosen. */
    noNumber: "–",
    /** SPEC-29: a 0–10 scale before her first tap. */
    scaleIdle: "Toca ou arrasta",
    less: "Menos",
    more: "Mais",
    hour: "Hora",
    minutes: "Minutos",
    hourPlaceholder: "hh",
    minutesPlaceholder: "mm",
    timeInvalid: "Escreve uma hora entre 00:00 e 23:59.",
    loading: "A carregar o questionário",
    saveFailed: "Não consegui guardar esta resposta no telemóvel. Tenta outra vez.",
    // SPEC-28: the recap before « Confirmar respostas », and the same screen read-only once done.
    recap: {
      pending: "Por confirmar",
      done: "✓ Feito",
      kicker: (instrument: string, title: string) => `${instrument} · ${title}`,
      title: "Confirma as tuas respostas.",
      titleDone: "As tuas respostas.",
      count: (n: number, unit: { one: string; many: string }) => `${n} ${n === 1 ? unit.one : unit.many} · toca numa resposta para a alterar`,
      countDone: "Concluída · só leitura",
      warningTitle: "Depois de confirmar, não dá para voltar atrás",
      warning: "As respostas ficam guardadas e deixam de poder ser alteradas. Revê-as antes de confirmar.",
      doneTitle: "Avaliação concluída",
      doneText: "As respostas confirmadas não podem ser alteradas. Ficam no teu perfil de saúde, confidenciais.",
      change: "Alterar",
      confirm: "Confirmar respostas",
      saving: "A guardar…",
      failed: "Não consegui guardar. Tenta de novo.",
      back: "Voltar",
      /** A written answer left empty. */
      noComment: "Sem comentários",
      /** LEAF-Q: the part before the question, « 1 · Lesões — … ». */
      inSection: (section: string, title: string) => `${section} — ${title}`,
      scale: (n: number, max: number) => `${n} / ${max}`,
    },
  },
  // By assessment id; an assessment's words land with its card.
  questionnaires,
  // Stand-ins for screens other cards build: the assessments after ICIQ
  // and Sono (SPEC-20 to SPEC-24).
  placeholder: {
    soon: "Este ecrã ainda está a ser construído.",
    back: "Voltar às avaliações",
  },
  // SPEC-33: the PYF team's read-only Base de utilizadoras, at /admin and on
  // an admin.* host, and one athlete's profile below it (SPEC-34). Every
  // number and status is the server's (adminUsers); the search and the
  // filters are poweryourflow-lib's (adminFilter). The labels are kept in
  // sentence case and set in capitals by the screen, so a screen reader
  // reads words, not letters. The sign-in card takes its labels and errors
  // from signIn above. The search's ellipsis is ours: the UI gate asks
  // every placeholder to end with one.
  admin: {
    /** SPEC-38: the name of the PT · FR · EN control in the header and on the sign-in card. */
    language: "Idioma",
    skip: "Ir para a base",
    label: "Admin · dados de saúde confidenciais",
    sections: "Secções do admin",
    base: "Base de dados",
    profile: "Perfil",
    toApp: "App",
    title: "Base de utilizadoras.",
    signInLead: "Entra com a tua conta da equipa PYF.",
    denied: "Não tens acesso a esta área.",
    /** Under « Não tens acesso »: another account may sign in after it. */
    signOut: "Terminar sessão",
    signOutFailed: "Não consegui terminar a sessão. Tenta outra vez.",
    loading: "A carregar a base…",
    failed: "Não consegui carregar a base. Tenta outra vez.",
    retry: "Tentar outra vez",
    searchLabel: "Procurar utilizadoras",
    search: "Procurar por nome, email ou clube…",
    export: "Exportar CSV",
    exporting: "A exportar…",
    exportFailed: "Não consegui exportar. Tenta outra vez.",
    stats: {
      accounts: "Utilizadoras",
      newAccounts: (n: number) => (n === 1 ? "1 nova nos últimos 30 dias" : `${n} novas nos últimos 30 dias`),
      done: "Avaliações concluídas",
      possible: (possible: number) => `/ ${possible}`,
      /** BRAND.md §5: a space before the percent sign, here one that never breaks. */
      donePercent: (percent: number) => `${percent} % do total possível`,
      inProgress: "Em curso",
      halfway: (n: number) => (n === 1 ? "deixada a meio" : "deixadas a meio"),
      alerts: "Com alerta",
      aboveThreshold: "resultado acima do limiar",
    },
    count: (n: number) => (n === 1 ? "1 utilizadora" : `${n} utilizadoras`),
    filtersLabel: "Filtrar utilizadoras",
    filters: { all: "Todas", alert: "Com alerta", progress: "Em curso", inactive: "Inativas" },
    caption: "Utilizadoras da PYF",
    columns: {
      user: "Utilizadora",
      club: "Clube · modalidade",
      level: "Nível",
      assessments: "Avaliações",
      dates: "Criada · última atividade",
      status: "Estado",
    },
    /** A value the account does not have. */
    none: "—",
    noClub: "Sem clube",
    /** « Futebol · 25 anos », with the parts that exist. */
    detail: (parts: readonly string[]) => parts.join(" · "),
    years: (age: number) => `${age} anos`,
    of: (total: number) => `/ ${total}`,
    /** « 21 set ». */
    date: (day: number, month: string) => `${day} ${month}`,
    statuses: { alert: "Alerta", inactive: "Inativa", progress: "Em curso", complete: "✓ Completo", active: "Ativa" },
    empty: "Nenhuma utilizadora corresponde.",
    more: "Mostrar mais",
    // SPEC-34: one athlete's profile and results, opened from her row of
    // the base at ?utilizadora={id}. Every number, day, status and result
    // is the server's (adminAccount). The names and kickers of the
    // assessments are the hub's (assessments.rows), her answers are worded
    // by the athlete app's recap, and Dados gerais by its own labels
    // (profile.rows, profileOnboarding). The page's loading, failure and
    // not-found sentences are ours, given on the card.
    athlete: {
      skip: "Ir para o perfil",
      back: "Base de utilizadoras",
      loading: "A carregar o perfil…",
      failed: "Não consegui carregar o perfil. Tenta outra vez.",
      notFound: "Esta utilizadora não existe.",
      /** « email · conta criada a 21 set · última atividade 18 out », with the parts that apply. */
      meta: (parts: readonly string[]) => parts.join(" · "),
      created: (date: string) => `conta criada a ${date}`,
      activity: (date: string) => `última atividade ${date}`,
      export: "Exportar respostas",
      exporting: "A exportar…",
      exportFailed: "Não consegui exportar. Tenta outra vez.",
      stats: {
        done: "Avaliações concluídas",
        toDo: (n: number) => (n === 0 ? "perfil de saúde completo" : `${n} por fazer`),
        inProgress: "Em curso",
        halfway: (n: number) => {
          if (n === 0) return "nenhuma a meio";
          if (n === 1) return "deixada a meio";
          return "deixadas a meio";
        },
        alerts: "Alertas",
        /** The instruments concerned, each once: « ICIQ-UI SF · Ciclo menstrual ». */
        instruments: (kickers: readonly string[]) => kickers.join(" · "),
        noAlert: "nenhum resultado acima do limiar",
        since: "Desde o registo",
        days: (n: number) => (n === 1 ? "dia" : "dias"),
        lastActivity: (days: number) => {
          if (days === 0) return "última atividade hoje";
          if (days === 1) return "última atividade há 1 dia";
          return `última atividade há ${days} dias`;
        },
      },
      generalData: "Dados gerais",
      notAnswered: "Ainda não respondeu.",
      privacyTitle: "Dados de saúde confidenciais",
      privacyText: "Visíveis só à equipa clínica. Nunca partilhados com clube ou treinador.",
      assessments: "Avaliações e resultados",
      doneMeta: (kicker: string, date: string) => `${kicker} · feito a ${date}`,
      progressMeta: (kicker: string, answered: number, total: number) => `${kicker} · ${answered} / ${total} respondidas`,
      todoMeta: (kicker: string) => `${kicker} · por fazer`,
      tags: { done: "✓ Feito", progress: "Em curso", todo: "Por fazer", alert: "Alerta" },
      /** « /21 », after the score of a row. */
      max: (max: number) => `/${max}`,
      /** « Resultado 11 / 21 · limiar 8 · acima do limiar », with the parts that apply. */
      result: (score: number, max: number, threshold: number | undefined, alert: boolean) => {
        const parts = [`Resultado ${score} / ${max}`];
        if (threshold !== undefined) parts.push(`limiar ${threshold}`);
        if (alert) parts.push("acima do limiar");
        return parts.join(" · ");
      },
      noScore: "Sem pontuação",
    },
    // SPEC-44 to SPEC-51: the bilan presencial, its body map and its form sections.
    bilan: bilanPt,
    cycle: cyclePt,
    bodyMap: bodyMapPt,
    bilanForm: bilanFormPt,
    athleteLanguage: athleteLanguagePt,
    newAccount: newAccountPt,
  },
} as const;
